import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { ACTION, AUDIENCE, lifecycleNotice, onboardingPath } from '../lib/lifecycle-notice.js';
import { consentReturnsHere, CONSENT_LINK_MISMATCH } from '../lib/consent-url.js';
import Notice from './Notice.jsx';
import { ReissueOnboardingLink, canIssueOnboardingLink } from './OnboardingLink.jsx';

const DEPLOY_POLL_MS = 10000;

// Lifecycle tones map onto the Notice rule (ENTRAID-347 redesign): a white card with a 4 px left rule, never a fill.
const NOTICE_TONE = Object.freeze({ amber: 'warn', red: 'bad', slate: 'info' });

/**
 * Renders the status-driven empty or waiting state for a customer view.
 * All wording comes from lib/lifecycle-notice.js, so every tab says the same
 * thing for the same status. Renders nothing when the customer is deployed
 * and has data.
 *
 * Props:
 *   customer   the customer record
 *   audience   'customer' (customer admin or "Viewing as client") | 'operator'
 *   hasData    true when at least one collection run exists
 *   onRefresh  reloads the customer record (Check again, and when a polled
 *              deployment finishes)
 *   readOnly   the viewer role (read-only Overview and Security): the words
 *              only, plus Check again and Contact support. No action that
 *              writes or opens a page the viewer cannot use, no deploy poll.
 */
const READ_ONLY_ACTIONS = new Set([ACTION.CHECK_AGAIN, ACTION.CONTACT_SUPPORT]);

export default function LifecycleNotice({ customer, audience = AUDIENCE.CUSTOMER, hasData = false, onRefresh, readOnly = false }) {
  const notice = lifecycleNotice(customer, { audience, hasData });
  if (!notice) return null;
  const tone = NOTICE_TONE[notice.tone] ?? 'info';
  const actions = readOnly ? notice.actions.filter((a) => READ_ONLY_ACTIONS.has(a.type)) : notice.actions;

  return (
    <Notice
      tone={tone}
      title={notice.title}
      titleAs="h3"
      role="region"
      dataAttrs={{ 'data-lifecycle-status': notice.status ?? 'unknown' }}
    >
      <p className="text-ipc-ink">{notice.message}</p>
      {notice.detail && (
        <p className="mt-2 break-words rounded border border-ipc-border px-2 py-1.5 font-mono text-xs text-ipc-ink">
          {notice.detail}
        </p>
      )}
      {notice.pollDeployStatus && !readOnly && <DeployProgress customerId={customer?.customerId} onFinished={onRefresh} />}
      {actions.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          {actions.map((a) => (
            <NoticeAction key={a.type} action={a} customer={customer} onRefresh={onRefresh} />
          ))}
        </div>
      )}
    </Notice>
  );
}

function NoticeAction({ action, customer, onRefresh }) {
  switch (action.type) {
    case ACTION.CONTINUE_ONBOARDING:
      return <Link className="btn-primary text-sm" to={action.href}>{action.label}</Link>;
    case ACTION.CHECK_AGAIN:
      return (
        <button type="button" className="text-sm text-ipc-light hover:underline" onClick={() => onRefresh?.()}>
          {action.label}
        </button>
      );
    case ACTION.CONTACT_SUPPORT:
      return (
        <span className="text-sm text-ipc-muted">
          Need help? <a className="text-ipc-light hover:underline" href={action.href}>{action.label}</a>
        </span>
      );
    case ACTION.COPY_ONBOARDING_LINK:
      // No admin login exists yet (no claim made): the wizard URL below only
      // opens after sign-in, so it would strand the admin on the login page
      // with no account (the 2026-10-22 TEST rehearsal). Issue the single-use
      // claim link instead, which creates their login (ENTRAID-350). This
      // action is offered to operators only; the server checks the role.
      if (canIssueOnboardingLink(customer)) {
        return (
          <ReissueOnboardingLink
            customer={customer}
            isOperator
            label="Issue onboarding link"
            className="btn-primary text-sm"
          />
        );
      }
      return (
        <CopyLink
          label={action.label}
          resolve={async () => `${window.location.origin}${onboardingPath(customer?.customerId)}`}
        />
      );
    case ACTION.COPY_CONSENT_LINK:
      return <ConsentCopyLink customer={customer} label={action.label} />;
    default:
      return null;
  }
}

/** The copy-consent-link button: a fresh admin-consent sign-in link (also used for re-consent, ENTRAID-307). */
export function ConsentCopyLink({ customer, label }) {
  return (
    <CopyLink
      label={label}
      resolve={async () => {
        // A fresh admin-consent sign-in link (SEC-001, ENTRAID-261): the
        // tenant comes from the Global Administrator's sign-in, and earlier
        // links stay valid, so nothing about the customer changes here.
        const r = await api.startConsent(customer.customerId, {});
        if (!consentReturnsHere(r?.consentUrl)) throw new Error(CONSENT_LINK_MISMATCH);
        return r.consentUrl;
      }}
    />
  );
}

/** Resolves a link, copies it, and always shows it so it can be copied by hand. */
function CopyLink({ label, resolve }) {
  const [state, setState] = useState({ busy: false, url: null, copied: false, error: null });

  async function onClick() {
    setState({ busy: true, url: null, copied: false, error: null });
    let url;
    try {
      url = await resolve();
    } catch (e) {
      setState({ busy: false, url: null, copied: false, error: e.message });
      return;
    }
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        copied = true;
      }
    } catch {
      copied = false;
    }
    setState({ busy: false, url, copied, error: null });
  }

  return (
    <div className="flex flex-col gap-1.5 w-full sm:w-auto">
      <button type="button" className="btn-primary text-sm self-start disabled:opacity-60" onClick={onClick} disabled={state.busy}>
        {state.busy ? 'Getting link…' : label}
      </button>
      {state.url && (
        <>
          <p className="text-xs text-ipc-ink" role="status">
            {state.copied ? 'Copied to the clipboard.' : 'Copy the link below.'}
          </p>
          <input
            readOnly
            aria-label="Link to send"
            value={state.url}
            onFocus={(e) => e.target.select()}
            className="input font-mono text-xs w-full sm:w-96"
          />
        </>
      )}
      {state.error && <p className="text-xs text-ipc-bad" role="alert">{state.error}</p>}
    </div>
  );
}

/**
 * Deployment progress for a "deploying" customer. Polling deploy-status also
 * finalizes the deployment on the backend once the stack completes, so when
 * the status leaves "deploying" we reload the customer record.
 */
function DeployProgress({ customerId, onFinished }) {
  const [info, setInfo] = useState(null);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  useEffect(() => {
    if (!customerId) return undefined;
    let stopped = false;
    async function poll() {
      try {
        const s = await api.getDeployStatus(customerId);
        if (stopped) return;
        setInfo(s);
        if (s?.status && s.status !== 'deploying') finishedRef.current?.();
      } catch {
        // Transient poll errors keep the last known progress on screen.
      }
    }
    poll();
    const t = setInterval(poll, DEPLOY_POLL_MS);
    return () => { stopped = true; clearInterval(t); };
  }, [customerId]);

  const minutes = info?.elapsedSeconds != null ? Math.max(1, Math.round(info.elapsedSeconds / 60)) : null;
  return (
    <p className="mt-2 text-xs text-ipc-muted" role="status" aria-live="polite">
      {info?.stackStatus && info.stackStatus !== 'polling-error'
        ? `AWS CloudFormation: ${stackStatusLabel(info.stackStatus)}`
        : 'Checking deployment progress…'}
      {minutes != null && `, ${minutes} min elapsed`}
    </p>
  );
}

function stackStatusLabel(stackStatus) {
  if (/ROLLBACK/.test(stackStatus)) return 'rolling back';
  if (/_IN_PROGRESS$/.test(stackStatus)) return 'creating resources';
  if (/_COMPLETE$/.test(stackStatus)) return 'finishing';
  return stackStatus.toLowerCase().replace(/_/g, ' ');
}

/** Read permissions the consent does not yet cover (ENTRAID-307, ENTRAID-315): the names and the re-consent action. */
export function MissingPermissionsNote({ customer, onRefresh }) {
  if (!(customer?.missingPermissions?.length > 0)) return null;
  return (
    <Notice
      tone="warn"
      title="Additional read permissions are available for compliance reports"
      titleAs="p"
      testId="missing-permissions"
      className="mt-4"
    >
      <ul className="mt-1 list-disc pl-5 text-ipc-ink">
        {customer.missingPermissions.map((name) => <li key={name}><code>{name}</code></li>)}
      </ul>
      <p className="mt-1">Compliance controls that depend on these permissions stay Not assessed until they are granted.</p>
      <div className="mt-2"><ConsentCopyLink customer={customer} label="Grant additional permissions" /></div>
    </Notice>
  );
}
